import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPrescriptions, updatePrescription, deletePrescription } from '../../services/prescriptionApi';
import toast from 'react-hot-toast';
import { FileText, Plus, Search, Filter, ChevronDown, ChevronUp, Pill, Loader2, Edit3, Trash2 } from 'lucide-react';
import Pagination from '../../Components/common/Pagination';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  pending_verification: 'bg-orange-50 text-orange-700 border-orange-200',
  verified: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  sent_to_lead: 'bg-blue-50 text-blue-700 border-blue-200',
  rejected: 'bg-rose-50 text-rose-700 border-rose-200'
};

const PrescriptionList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [search, statusFilter]);

  useEffect(() => {
    fetchPrescriptions();
  }, [pagination.page, statusFilter]);

  const fetchPrescriptions = async () => {
    try {
      setLoading(prescriptions.length === 0);
      setIsFetching(prescriptions.length !== 0);
      const params = { page: pagination.page, limit: pagination.limit, status: statusFilter || undefined };
      if (search) params.search = search;
      const response = await getPrescriptions(params);
      const data = response?.data || response;
      if (data && typeof data === 'object') {
        setPrescriptions(Array.isArray(data.prescriptions) ? data.prescriptions : []);
        setPagination(prev => ({
          ...prev,
          total: Number(data.total) || 0,
          pages: Number(data.pages) || 0
        }));
        setTotal(Number(data.total) || 0);
        setTotalPages(Number(data.pages) || 1);
      }
    } catch (error) {
      console.error('[PrescriptionList] fetch error:', error);
      toast.error(error.response?.data?.message || 'Failed to load prescriptions');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPrescriptions();
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status === statusFilter ? '' : status);
    setCurrentPage(1);
  };

  const getStatusBadge = (status) => {
    const displayStatus = status || 'unknown';
    const style = STATUS_COLORS[status] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${style}`}>
        {displayStatus.replace(/_/g, ' ')}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const canCreate = ['admin', 'customer_care', 'doctor'].includes(user?.role);
  const canManagePrescription = ['admin', 'customer_care', 'cc_head'].includes(user?.role);

  const canEditPrescription = (prescription) => {
    if (!prescription || !user) return false;
    const isAdmin = user.role === 'admin';
    const isCC = user.role === 'customer_care' || user.role === 'cc_head';
    const isOwner = prescription.prescribedBy?._id?.toString() === user._id?.toString();
    return (isAdmin || isCC || isOwner) && ['draft', 'rejected', 'sent_to_lead'].includes(prescription.status);
  };

  const handleEdit = (prescription) => {
    if (!canEditPrescription(prescription)) {
      toast.error('You do not have permission to edit this prescription');
      return;
    }
    navigate(`/prescriptions/${prescription._id}?edit=true`, { state: { appointmentId: prescription.appointmentId?._id } });
  };

  const openDeleteModal = (id) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    setDeleting(true);
    try {
      await deletePrescription(deletingId);
      toast.success('Prescription deleted successfully');
      setShowDeleteModal(false);
      setDeletingId(null);
      fetchPrescriptions();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete prescription');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-10 w-40 bg-slate-200 animate-pulse rounded-2xl"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#143E78]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Prescriptions</h1>
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1">
            Manage and track prescription forms
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canCreate && (
            <button
              onClick={() => navigate('/prescriptions/create')}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#143E78] text-white rounded-2xl text-xs font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20"
            >
              <Plus size={16} />
              New Prescription
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-2">
        <div className="flex flex-wrap gap-1">
          {['draft', 'submitted', 'pending_verification', 'verified', 'sent_to_lead', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => handleStatusFilter(status)}
              className={`px-4 py-2.5 rounded-2xl text-xs font-black uppercase tracking-wider transition-all ${
                statusFilter === status
                  ? 'bg-[#143E78] text-white shadow-lg shadow-[#143E78]/20'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              {status.replace(/_/g, ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <form onSubmit={handleSearch} className="flex w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by medicine name or notes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
              />
            </form>
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center gap-2 px-4 py-3.5 rounded-2xl border-2 transition-all text-xs font-black uppercase tracking-widest ${
              showFilters
                ? 'bg-[#143E78] border-[#143E78] text-white shadow-lg shadow-[#143E78]/20'
                : 'bg-white border-slate-200 text-slate-600 hover:border-[#143E78] hover:text-[#143E78]'
            }`}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Filters'}
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-6 border-t-2 border-dashed border-slate-200">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Pill size={14} className="text-[#143E78]" />
                Status
              </label>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilter(e.target.value)}
                  className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
                >
                  <option value="">All Statuses</option>
                  {['draft', 'submitted', 'pending_verification', 'verified', 'sent_to_lead', 'rejected'].map((status) => (
                    <option key={status} value={status}>{status.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
          Showing {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
        </p>
        {isFetching && (
          <div className="flex items-center gap-2 text-[10px] font-black text-[#143E78] uppercase tracking-widest">
            <Loader2 size={14} className="animate-spin" />
            Updating...
          </div>
        )}
      </div>

      <div
        className={`bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden transition-all duration-300 ${isFetching ? 'opacity-60' : 'opacity-100'}`}
      >
        <div className="overflow-x-auto">
          {prescriptions.length === 0 ? (
            <div className="px-6 py-16 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mb-4 border-2 border-dashed border-slate-200">
                  <FileText size={28} className="text-slate-300" />
                </div>
                <p className="text-sm font-black text-slate-500 uppercase tracking-wider">No prescriptions found</p>
                <p className="text-xs font-bold text-slate-400 mt-1">Try adjusting your filters or search criteria</p>
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-slate-100">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Appointment ID</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Prescription ID</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Patient</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Doctor</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Status</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Items</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Date</th>
                  <th className="px-6 py-4 text-right text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {prescriptions.map((prescription) => (
                  <tr
                    key={prescription._id}
                     onClick={(e) => {
                       if (e.target.closest('a') || e.target.closest('button')) return;
                       navigate(`/prescriptions/${prescription._id}`, { state: { appointmentId: prescription.appointmentId?._id } });
                     }}
                    className="group hover:bg-slate-50/80 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4">
                      <span className="text-sm font-black text-[#143E78] group-hover:underline">
                        {prescription.appointmentId?.bookingId || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-black text-slate-900">
                        #{prescription._id.slice(-8).toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-black text-slate-900">{prescription.leadId?.patientName || '-'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#143E78] shrink-0"></span>
                        <span className="truncate">
                          {prescription.doctorId?.user?.firstName ? `Dr. ${prescription.doctorId.user.firstName} ${prescription.doctorId.user.lastName}` : '-'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(prescription.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-slate-700">
                        {prescription.totalItems || prescription.prescriptionItems?.length || 0} item(s)
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-slate-900">{formatDate(prescription.createdAt)}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canEditPrescription(prescription) && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleEdit(prescription); }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-50 text-amber-700 border border-amber-200 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-amber-100 transition-all"
                          >
                            <Edit3 size={14} /> Edit
                          </button>
                        )}
                        {canManagePrescription && (
                          <button
                            onClick={(e) => { e.stopPropagation(); openDeleteModal(prescription._id); }}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-rose-100 transition-all"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/prescriptions/${prescription._id}`, { state: { appointmentId: prescription.appointmentId?._id } }); }}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#143E78] text-white rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-[#0f2f5e] transition-all shadow-lg shadow-[#143E78]/20"
                        >
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-2">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Showing {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
          </div>
          <div className="w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
            />
          </div>
        </div>
      )}
      
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-6 sm:p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-rose-50 rounded-2xl border border-rose-100">
                <Trash2 className="w-6 h-6 text-rose-600" />
              </div>
              <div>
                <h3 className="text-lg font-black text-slate-900">Delete Prescription</h3>
                <p className="text-xs font-bold text-slate-500">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 font-medium mb-6 leading-relaxed">
              Are you sure you want to delete this prescription? All associated data will be permanently removed.
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-2xl text-xs font-black hover:bg-rose-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleting && <Loader2 size={14} className="animate-spin" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionList;
